<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import TermsCheckbox from '@uiKit/components/TermsCheckbox.vue'
import { SELL_TERMS_LABEL } from '../constants/sellCopy'

// Sticky CTA footer of the sell drawer (Figma "CTA Footer", 34796:138873).
// On Confirm Listing it adds the terms tick and an inline error above the
// button.

interface Props {
  label: string
  /** Show the terms tick (Confirm Listing step). */
  hasTerms: boolean
  error?: string | null
  loading?: boolean
  disabled?: boolean
}

withDefaults(defineProps<Props>(), { error: null, loading: false, disabled: false })

const hasAcceptedTerms = defineModel<boolean>('terms', { required: true })

const emit = defineEmits<{ (e: 'submit'): void }>()
</script>

<template>
  <Box direction="col" gap="10px" class="sell-footer">
    <TermsCheckbox v-if="hasTerms" v-model="hasAcceptedTerms" size="lg" class="sell-footer__terms">
      <Text is="span" type="caption" color="var(--p-text-muted-color)" style="line-height: 1.5"
        >{{ SELL_TERMS_LABEL.before
        }}<Text
          is="span"
          type="caption"
          weight="medium"
          color="var(--p-primary-500)"
          style="text-decoration: underline; text-underline-position: from-font"
          >{{ SELL_TERMS_LABEL.link }}</Text
        >{{ SELL_TERMS_LABEL.after }}</Text
      >
    </TermsCheckbox>
    <Text v-if="error" is="span" type="caption" color="danger" role="alert">{{ error }}</Text>
    <Button
      :label="label"
      size="large"
      block
      :loading="loading"
      :disabled="disabled || loading"
      @click="emit('submit')"
    />
  </Box>
</template>

<style scoped>
.sell-footer {
  position: sticky;
  bottom: 0;
  padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--p-sliceal-gray-200);
  background: var(--p-surface-0);
}

.sell-footer__terms {
  min-height: 44px;
  padding-top: 6px;
  align-items: flex-start;
  box-sizing: border-box;
}

@media (max-width: 992px) {
  .sell-footer {
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
  }
}
</style>
